// The first thing the page runs (qa-runs 1227), importing nothing, so a failure while anything else loads is still seen.
// It holds errors and console lines in memory, a short list and no more. Nothing leaves the page from here; `diagnostics.ts`
// takes what is held only if the table is being recorded.
export interface Captured { kind: string; payload: unknown; t: number }

const HOLD = 300;
const held: Captured[] = [];
let sink: ((c: Captured) => void) | null = null;
let onHeldError: (() => void) | null = null;

/** One thing that happened. Recording: passed on. Not: an error or console line is held, and the rest is forgotten. */
export function capture(kind: string, payload: unknown): void {
  const c = { kind, payload, t: Date.now() };
  if (sink) { sink(c); return; }
  if (!kind.startsWith('client.error') && !kind.startsWith('client.rejection') && !kind.startsWith('client.react') && !kind.startsWith('client.console') && !kind.startsWith('client.asset')) return;
  held.push(c);
  if (held.length > HOLD) held.shift();
  if (!kind.startsWith('client.console.warn')) onHeldError?.();
}

/** Recording started: everything held goes to `to`, and so does everything from now on. */
export function captureTo(to: (c: Captured) => void): void {
  sink = to;
  for (const c of held.splice(0)) to(c);
}
export function captureStop(): void { sink = null; }
/** What was held from before the DM turned recording on: not part of it. */
export function captureForget(): void { held.length = 0; }
/** Told of an error held while nobody knows whether the table is recorded; told at once of one already held. */
export function onError(fn: () => void): void {
  onHeldError = fn;
  if (held.some((c) => !c.kind.startsWith('client.console.warn'))) fn();
}

const text = (v: unknown): string => {
  if (v instanceof Error) return `${v.name}: ${v.message}`;
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v) ?? String(v); } catch { return String(v); }
};

if (typeof window !== 'undefined') {
  // In the capture phase, because a picture or script that failed to load does not bubble.
  window.addEventListener('error', (e: ErrorEvent) => {
    const el = e.target as (Element & { src?: string; href?: string }) | null;
    if (el && el !== (window as unknown) && el.tagName) {
      capture('client.asset', { tag: el.tagName.toLowerCase(), src: (el.src ?? el.href ?? '').split('?')[0] });
      return;
    }
    capture('client.error', { message: e.message, source: e.filename, line: e.lineno, column: e.colno, stack: e.error instanceof Error ? e.error.stack : undefined });
  }, true);
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    capture('client.rejection', { message: text(e.reason), stack: e.reason instanceof Error ? e.reason.stack : undefined });
  });
  for (const level of ['error', 'warn'] as const) {
    const real = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      real(...args);
      capture(`client.console.${level}`, { text: args.map(text).join(' ') });
    };
  }
}
